import {
  Autocomplete,
  autocompleteClasses,
  Popper,
  type SxProps,
  TextField,
  type Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  List,
  type RowComponentProps,
  useDynamicRowHeight,
} from 'react-window';

import { useTheme } from './theme';

export const AUTO_TIMEZONE = 'auto';

const TIMEZONE_VALUES = Intl.supportedValuesOf('timeZone');
const LISTBOX_PADDING = 8;

export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function getTimezoneLabel(timeZone: string): string {
  const offsetPart = new Intl.DateTimeFormat('en', {
    timeZone,
    timeZoneName: 'shortOffset',
  })
    .formatToParts()
    .find((part) => part.type === 'timeZoneName');
  const namePart = new Intl.DateTimeFormat('en', {
    timeZone,
    timeZoneName: 'long',
  })
    .formatToParts()
    .find((part) => part.type === 'timeZoneName');

  const offset = offsetPart?.value ?? '';
  const name = namePart?.value ?? '';

  return `${timeZone} - ${name} (${offset})`;
}

type OptionData = [
  React.HTMLAttributes<HTMLLIElement> & { key: string },
  string,
];

function RowComponent({
  index,
  itemData,
  style,
}: RowComponentProps & { itemData: OptionData[] }) {
  const [props, label] = itemData[index];
  const inlineStyle = {
    ...style,
    top: ((style.top as number) ?? 0) + LISTBOX_PADDING,
  };

  return (
    <Typography component="li" {...props} style={inlineStyle} sx={{ px: 2 }}>
      {label}
    </Typography>
  );
}

interface ListRef {
  readonly element: HTMLDivElement | null;
  scrollToRow: (config: {
    index: number;
    align?: 'auto' | 'center' | 'end' | 'start' | 'smart';
    behavior?: 'auto' | 'smooth' | 'instant';
  }) => void;
}

const ListboxComponent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLElement> & {
    internalListRef: React.RefObject<ListRef | null>;
    onItemsBuilt: (optionIndexMap: Map<string, number>) => void;
  }
>(function ListboxComponent(props, ref) {
  const { children, internalListRef, onItemsBuilt, ...other } = props;
  const [theme] = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const itemData = children as OptionData[];
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const optionIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    itemData.forEach((item, index) => {
      if (Array.isArray(item) && item[1]) {
        map.set(item[1], index);
      }
    });
    return map;
  }, [itemData]);

  useEffect(() => {
    onItemsBuilt(optionIndexMap);
  }, [onItemsBuilt, optionIndexMap]);

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemCount * itemSize;
  };

  const { className, ...otherProps } = other;

  const rowHeight = useDynamicRowHeight({
    defaultRowHeight: 45,
  });

  return (
    <div ref={ref} {...otherProps}>
      <List
        className={className}
        listRef={internalListRef as React.Ref<ListRef>}
        rowCount={itemCount}
        rowHeight={rowHeight}
        rowComponent={RowComponent}
        rowProps={{ itemData }}
        style={{
          height: getHeight() + 2 * LISTBOX_PADDING,
          width: '100%',
        }}
        overscanCount={5}
        tagName="ul"
      />
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

interface TimezoneAutocompleteProps {
  value: string | null;
  onChange: (value: string | null) => void;
  readOnly?: boolean;
  label?: string;
  required?: boolean;
  error?: boolean;
  sx?: SxProps<Theme>;
  showAutoOption?: boolean;
  helperText?: string;
}

const TimezoneAutocomplete = forwardRef<
  HTMLDivElement,
  TimezoneAutocompleteProps
>(function TimezoneAutocomplete(
  {
    value,
    onChange,
    readOnly = false,
    required = false,
    error = false,
    label = 'Timezone',
    sx,
    showAutoOption = false,
    helperText = 'Automatically detected from address or manually selectable.',
  },
  ref
) {
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const internalListRef = useRef<ListRef | null>(null);
  const optionIndexMapRef = useRef<Map<string, number>>(new Map());

  const options = useMemo(() => {
    if (showAutoOption) {
      return [AUTO_TIMEZONE, ...TIMEZONE_VALUES];
    }
    return TIMEZONE_VALUES;
  }, [showAutoOption]);

  const timezoneLabels = useMemo(() => {
    const labels = new Map<string, string>();
    if (showAutoOption) {
      labels.set(
        AUTO_TIMEZONE,
        `Auto - Use browser timezone (${browserTimezone})`
      );
    }
    for (const tz of TIMEZONE_VALUES) {
      labels.set(tz, getTimezoneLabel(tz));
    }
    return labels;
  }, [showAutoOption, browserTimezone]);

  const handleItemsBuilt = useCallback(
    (optionIndexMap: Map<string, number>) => {
      optionIndexMapRef.current = optionIndexMap;
    },
    []
  );

  const handleHighlightChange = useCallback(
    (_event: SyntheticEvent, option: string | null) => {
      if (option && internalListRef.current) {
        const optionLabel = timezoneLabels.get(option) ?? option;
        const index = optionIndexMapRef.current.get(optionLabel);
        if (index !== undefined) {
          internalListRef.current.scrollToRow({ index, align: 'auto' });
        }
      }
    },
    [timezoneLabels, internalListRef]
  );

  return (
    <Autocomplete
      ref={ref}
      autoHighlight
      openOnFocus
      disableListWrap
      options={options}
      value={value || null}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      getOptionLabel={(option) => timezoneLabels.get(option) ?? option}
      filterOptions={(allOptions, { inputValue }) => {
        const search = inputValue.toLowerCase();
        return allOptions.filter((tz) => {
          const optionLabel = timezoneLabels.get(tz) ?? tz;
          return optionLabel.toLowerCase().includes(search);
        });
      }}
      readOnly={readOnly}
      sx={sx}
      onHighlightChange={handleHighlightChange}
      renderOption={(props, option) =>
        [props, timezoneLabels.get(option) ?? option] as ReactNode
      }
      slots={{
        popper: StyledPopper,
      }}
      slotProps={{
        listbox: {
          component: ListboxComponent,
          internalListRef,
          onItemsBuilt: handleItemsBuilt,
        } as object,
      }}
      renderInput={(params) => (
        <TextField
          helperText={helperText || undefined}
          {...params}
          required={required}
          error={error}
          label={label}
          variant="outlined"
          fullWidth
        />
      )}
    />
  );
});

export default TimezoneAutocomplete;
